/**
 * Lotes de productos importados: lista los lotes que grabó la importación de
 * datos (chasis con nfactura IMPORTACION, agrupados por fecha y usuario) y
 * permite corregir sus filas. El backend aplica las reglas del Registro de
 * Chasis: préstamo activo bloquea la fila, vendido congela costo/precios y
 * en consignación el suplidor no se cambia.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import Alert from "@mui/material/Alert";
import Chip from "@mui/material/Chip";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import LinearProgress from "@mui/material/LinearProgress";
import PropTypes from "prop-types";
import { DataGrid } from "@mui/x-data-grid";
import MDButton from "components/MDButton";
import { useApi, fechaDMY } from "lib/api";
import { fpInputSx } from "components/financepro";

const SIN_SUPLIDOR = "(Sin suplidor)";
const CAMPOS_PRECIO = ["costo", "precio", "mprecio"];
// Campos que viajan al backend (mismo formato que /api/ares1010_modificar).
const CAMPOS_ENVIO = [
  "chasis",
  "marca",
  "modelo",
  "colore",
  "costo",
  "precio",
  "mprecio",
  "condicion",
  "ano",
  "cilindraje",
  "maquina",
  "suplidor",
  "empresa",
];

const hoyISO = () => new Date().toISOString().slice(0, 10);
const haceDiasISO = (dias) => {
  const d = new Date();
  d.setDate(d.getDate() - dias);
  return d.toISOString().slice(0, 10);
};

// "2026-11-01T14:05:09" -> "01/11/2026 14:05"; una fecha sola queda dd/mm/aaaa.
const fechaHora = (v) => {
  const texto = String(v || "");
  const hora = texto.match(/T(\d{2}:\d{2})/);
  return hora ? `${fechaDMY(texto)} ${hora[1]}` : fechaDMY(texto);
};

const nombreCarga = (l) => {
  if (!l?.carga) return "Carga anterior (agrupada por día)";
  return l.archivo ? `#${l.carga} · ${l.archivo}` : `Carga #${l.carga}`;
};

const formatoMonto = (v) =>
  v == null || v === ""
    ? ""
    : Number(v).toLocaleString("es-DO", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

function mismaFila(a, b) {
  return CAMPOS_ENVIO.every((k) => String(a?.[k] ?? "") === String(b?.[k] ?? ""));
}

/** Motivo por el que un campo de la fila no se puede editar, o null. */
function bloqueoCampo(row, field) {
  if (field === "chasis") return "El chasis no se cambia desde aquí";
  if (row.prestamoactivo) return `Tiene el préstamo activo No. ${row.noprest}`;
  if (row.vendido && CAMPOS_PRECIO.includes(field)) return "Vendido: costo y precios congelados";
  if (row.consignado && field === "suplidor") return "Consignación: el suplidor es el consignante";
  return null;
}

function LotesProductosImportados({ open, onClose, sucursalOptions = [], suplidorOptions = [] }) {
  const { post } = useApi();
  const [desde, setDesde] = useState(haceDiasISO(90));
  const [hasta, setHasta] = useState(hoyISO());
  const [lotes, setLotes] = useState([]);
  const [cargandoLotes, setCargandoLotes] = useState(false);
  const [lote, setLote] = useState(null); // {fecha, creador}
  const [filas, setFilas] = useState([]);
  const [originales, setOriginales] = useState({});
  const [config, setConfig] = useState({});
  const [cargandoDetalle, setCargandoDetalle] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState("");
  const [resultado, setResultado] = useState(null);
  const [masivo, setMasivo] = useState({ campo: "precio", valor: "" });
  // Candado contra doble clic en Guardar: el estado tarda un render.
  const guardandoRef = useRef(false);

  const nombreSucursal = useMemo(() => {
    const mapa = {};
    sucursalOptions.forEach((s) => {
      const label = String(s?.label || "");
      mapa[label.split("-")[0].trim()] = label;
    });
    return (codigos) =>
      String(codigos || "")
        .split(",")
        .map((c) => mapa[c.trim()] || c.trim())
        .filter(Boolean)
        .join(", ");
  }, [sucursalOptions]);

  const buscarLotes = useCallback(async () => {
    setCargandoLotes(true);
    setError("");
    const { ok, data } = await post("/api/importacion/productos/lotes", { desde, hasta });
    setCargandoLotes(false);
    if (!ok || !data?.ok) {
      setError(data?.message || data?.Error || "No se pudieron leer los lotes.");
      setLotes([]);
      return;
    }
    setLotes(data.data?.lotes || []);
  }, [post, desde, hasta]);

  useEffect(() => {
    if (open) buscarLotes();
    // Solo al abrir: los cambios de fecha se aplican con el botón Buscar.
  }, [open]);

  const abrirLote = useCallback(
    async (l) => {
      setLote(l);
      setFilas([]);
      setOriginales({});
      setResultado(null);
      setError("");
      setCargandoDetalle(true);
      const { ok, data } = await post(
        "/api/importacion/productos/lote_detalle",
        l.carga ? { carga: l.carga } : { fecha: l.fecha, creador: l.creador }
      );
      setCargandoDetalle(false);
      if (!ok || !data?.ok) {
        setError(data?.message || data?.Error || "No se pudo leer el lote.");
        return;
      }
      const productos = (data.data?.productos || []).map((p) => ({
        ...p,
        id: p.chasis,
        empresa: p.empresa != null ? String(p.empresa) : undefined,
        // La importación graba 0 cuando no hay suplidor.
        suplidor: p.suplidor === "0" ? "" : p.suplidor,
      }));
      setFilas(productos);
      setOriginales(Object.fromEntries(productos.map((p) => [p.chasis, p])));
      setConfig(data.data?.config || {});
    },
    [post]
  );

  const sucios = useMemo(
    () => filas.filter((f) => originales[f.chasis] && !mismaFila(f, originales[f.chasis])),
    [filas, originales]
  );

  const confirmarDescarte = () =>
    sucios.length === 0 ||
    window.confirm(`Hay ${sucios.length} fila(s) con cambios sin guardar. ¿Descartarlos?`);

  const volver = () => {
    if (!confirmarDescarte()) return;
    setLote(null);
    setFilas([]);
    setOriginales({});
    setResultado(null);
    setError("");
  };

  const cerrar = () => {
    if (!confirmarDescarte()) return;
    setLote(null);
    setFilas([]);
    setOriginales({});
    setResultado(null);
    setError("");
    onClose?.();
  };

  const processRowUpdate = useCallback((nueva) => {
    const limpia = { ...nueva };
    ["marca", "modelo", "colore", "maquina"].forEach((k) => {
      if (typeof limpia[k] === "string") limpia[k] = limpia[k].trim().toUpperCase();
    });
    setFilas((prev) => prev.map((f) => (f.id === limpia.id ? limpia : f)));
    setResultado(null);
    return limpia;
  }, []);

  const deshacer = () => {
    setFilas((prev) => prev.map((f) => originales[f.chasis] || f));
    setResultado(null);
  };

  const camposMasivos = useMemo(() => {
    const lista = [
      { value: "marca", label: "Marca" },
      { value: "modelo", label: "Modelo" },
      { value: "colore", label: "Color" },
    ];
    if (!config.ocultarcostos) lista.push({ value: "costo", label: "Costo" });
    lista.push({ value: "precio", label: "Precio" }, { value: "mprecio", label: "Precio mínimo" });
    if (config.pideAno) lista.push({ value: "ano", label: "Año" });
    if (config.pideCilindraje) lista.push({ value: "cilindraje", label: "Cilindraje" });
    lista.push({ value: "suplidor", label: "Suplidor" });
    if (config.multiempresa) lista.push({ value: "empresa", label: "Empresa" });
    return lista;
  }, [config]);

  const aplicarATodas = () => {
    const { campo, valor } = masivo;
    if (campo !== "suplidor" && String(valor).trim() === "") return;
    let saltadas = 0;
    const v = ["marca", "modelo", "colore"].includes(campo)
      ? String(valor).trim().toUpperCase()
      : valor;
    setFilas(
      filas.map((f) => {
        if (bloqueoCampo(f, campo)) {
          saltadas += 1;
          return f;
        }
        return { ...f, [campo]: v };
      })
    );
    setResultado(
      saltadas > 0
        ? { aviso: `${saltadas} fila(s) no se cambiaron porque ese campo está bloqueado en ellas.` }
        : null
    );
  };

  const guardar = async () => {
    if (sucios.length === 0 || guardandoRef.current) return;
    guardandoRef.current = true;
    setGuardando(true);
    setError("");
    const productos = sucios.map((f) => {
      const fila = {};
      CAMPOS_ENVIO.forEach((k) => {
        if (f[k] !== undefined) fila[k] = f[k];
      });
      return fila;
    });
    const { ok, data } = await post("/api/importacion/productos/lote_modificar", {
      productos,
    }).catch(() => ({ ok: false, data: { message: "No hubo respuesta del servidor." } }));
    guardandoRef.current = false;
    setGuardando(false);
    if (!ok || !data?.ok) {
      setError(data?.message || data?.Error || "No se pudieron guardar los cambios.");
      return;
    }
    const actualizados = data.data?.actualizados || [];
    const errores = data.data?.errors || [];
    // Las filas guardadas pasan a ser el nuevo original (con la marca/modelo
    // que haya estandarizado el backend); las que fallaron quedan sucias.
    const porChasis = Object.fromEntries(actualizados.map((a) => [a.chasis, a]));
    const nuevasFilas = filas.map((f) =>
      porChasis[f.chasis]
        ? { ...f, marca: porChasis[f.chasis].marca, modelo: porChasis[f.chasis].modelo }
        : f
    );
    setFilas(nuevasFilas);
    setOriginales((prev) => {
      const next = { ...prev };
      nuevasFilas.forEach((f) => {
        if (porChasis[f.chasis]) next[f.chasis] = f;
      });
      return next;
    });
    setResultado({ guardadas: actualizados.length, errores });
  };

  const columnasLotes = useMemo(
    () => [
      {
        field: "fecha",
        headerName: "Fecha",
        width: 150,
        valueFormatter: (v) => fechaHora(v),
      },
      {
        field: "archivo",
        headerName: "Carga",
        flex: 1.4,
        minWidth: 200,
        valueGetter: (v, row) => nombreCarga(row),
      },
      { field: "creador", headerName: "Importado por", flex: 1, minWidth: 140 },
      {
        field: "sucursales",
        headerName: "Sucursal(es)",
        flex: 1.4,
        minWidth: 160,
        valueGetter: (v) => nombreSucursal(v),
      },
      { field: "total", headerName: "Chasis", type: "number", width: 100 },
      { field: "vendidos", headerName: "Vendidos", type: "number", width: 110 },
    ],
    [nombreSucursal]
  );

  const columnasDetalle = useMemo(() => {
    const texto = (field, headerName, minWidth = 120) => ({
      field,
      headerName,
      minWidth,
      flex: 1,
      editable: true,
    });
    const monto = (field, headerName) => ({
      field,
      headerName,
      type: "number",
      minWidth: 110,
      flex: 0.8,
      editable: true,
      valueFormatter: (v) => formatoMonto(v),
    });
    const cols = [
      { field: "chasis", headerName: "Chasis", minWidth: 170, flex: 1.2 },
      texto("marca", "Marca"),
      texto("modelo", "Modelo"),
      texto("colore", "Color", 100),
    ];
    if (!config.ocultarcostos) cols.push(monto("costo", "Costo"));
    cols.push(monto("precio", "Precio"), monto("mprecio", "Precio mín."));
    if (config.pideAno) {
      cols.push({
        field: "ano",
        headerName: "Año",
        type: "number",
        width: 90,
        editable: true,
        valueFormatter: (v) => (v ? String(v) : ""),
      });
    }
    if (config.pideCilindraje) cols.push(texto("cilindraje", "Cilindraje", 100));
    if (config.tieneMaquina) cols.push(texto("maquina", "No. máquina", 130));
    cols.push({
      field: "suplidor",
      headerName: "Suplidor",
      type: "singleSelect",
      editable: true,
      minWidth: 190,
      flex: 1.3,
      valueOptions: [
        { value: "", label: SIN_SUPLIDOR },
        ...suplidorOptions.map((s) => ({ value: s.id, label: s.label })),
      ],
    });
    if (config.multiempresa) {
      cols.push({
        field: "empresa",
        headerName: "Empresa",
        type: "singleSelect",
        editable: true,
        minWidth: 140,
        valueOptions: (config.empresas || []).map((e) => ({
          value: String(e.empresa),
          label: `${e.empresa} — ${e.nombre || `Empresa ${e.empresa}`}`,
        })),
      });
    }
    cols.push({
      field: "estado",
      headerName: "Estado",
      minWidth: 150,
      sortable: false,
      renderCell: ({ row }) => {
        if (row.prestamoactivo)
          return <Chip size="small" color="warning" label={`Préstamo ${row.noprest}`} />;
        if (row.vendido) return <Chip size="small" color="info" label="Vendido" />;
        if (row.consignado) return <Chip size="small" label="Consignación" />;
        return <Chip size="small" variant="outlined" label="En inventario" />;
      },
    });
    return cols;
  }, [config, suplidorOptions]);

  const erroresPorChasis = useMemo(
    () => Object.fromEntries((resultado?.errores || []).map((e) => [e.chasis, e.message])),
    [resultado]
  );

  return (
    <Dialog
      open={open}
      onClose={cerrar}
      fullWidth
      maxWidth="xl"
      aria-labelledby="lotes-importados-title"
    >
      <DialogTitle
        id="lotes-importados-title"
        sx={{ pr: 6, display: "flex", alignItems: "center", gap: 1 }}
      >
        {lote && (
          <IconButton
            size="small"
            onClick={volver}
            aria-label="Volver a la lista de lotes"
            disabled={guardando}
          >
            <ArrowBackIcon fontSize="small" />
          </IconButton>
        )}
        {lote
          ? `${nombreCarga(lote)} — ${fechaHora(lote.fecha)}, ${lote.creador || "sin usuario"} (${
              filas.length
            } chasis)`
          : "Lotes de productos importados"}
        <IconButton
          onClick={cerrar}
          aria-label="Cerrar"
          sx={{ position: "absolute", right: 8, top: 8 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {!lote && (
          <>
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={2}
              sx={{ mb: 2 }}
              alignItems={{ sm: "center" }}
            >
              <TextField
                type="date"
                label="Desde"
                size="small"
                value={desde}
                onChange={(e) => setDesde(e.target.value)}
                InputLabelProps={{ shrink: true }}
                sx={fpInputSx}
              />
              <TextField
                type="date"
                label="Hasta"
                size="small"
                value={hasta}
                onChange={(e) => setHasta(e.target.value)}
                InputLabelProps={{ shrink: true }}
                sx={fpInputSx}
              />
              <MDButton
                variant="gradient"
                color="info"
                onClick={buscarLotes}
                disabled={cargandoLotes}
              >
                {cargandoLotes ? "Buscando..." : "Buscar"}
              </MDButton>
            </Stack>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Cada lote es una carga de archivo. Las importaciones hechas antes de que se
              registraran las cargas aparecen agrupadas por día y usuario. Pulse un lote para ver y
              corregir sus chasis.
            </Typography>
            <Box sx={{ height: 460 }}>
              <DataGrid
                rows={lotes}
                columns={columnasLotes}
                loading={cargandoLotes}
                onRowClick={({ row }) => abrirLote(row)}
                density="compact"
                disableRowSelectionOnClick
                localeText={{ noRowsLabel: "No hay lotes importados en ese rango" }}
                sx={{ "& .MuiDataGrid-row": { cursor: "pointer" } }}
              />
            </Box>
          </>
        )}

        {lote && (
          <>
            {cargandoDetalle && <LinearProgress sx={{ mb: 2 }} />}
            {config.ocultarcostos && (
              <Alert severity="info" sx={{ mb: 2 }}>
                Su rol no ve los costos: se conservan los que ya tiene cada chasis.
              </Alert>
            )}
            {resultado?.aviso && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                {resultado.aviso}
              </Alert>
            )}
            {resultado?.guardadas != null && (
              <Alert severity={resultado.errores.length ? "warning" : "success"} sx={{ mb: 2 }}>
                Se guardaron {resultado.guardadas} chasis.
                {resultado.errores.length > 0 && (
                  <>
                    {" "}
                    {resultado.errores.length} no se guardaron (quedan marcados en la grilla):
                    <ul style={{ margin: "4px 0 0 18px", padding: 0 }}>
                      {resultado.errores.slice(0, 10).map((e) => (
                        <li key={e.chasis}>
                          <strong>{e.chasis}</strong>: {e.message}
                        </li>
                      ))}
                    </ul>
                  </>
                )}
              </Alert>
            )}
            <Stack
              direction={{ xs: "column", md: "row" }}
              spacing={1.5}
              sx={{ mb: 2 }}
              alignItems={{ md: "center" }}
            >
              <Typography variant="body2" color="text.secondary">
                Cambiar en todas las filas:
              </Typography>
              <TextField
                select
                size="small"
                label="Campo"
                value={masivo.campo}
                onChange={(e) => setMasivo({ campo: e.target.value, valor: "" })}
                sx={{ ...fpInputSx, minWidth: 160 }}
              >
                {camposMasivos.map((c) => (
                  <MenuItem key={c.value} value={c.value}>
                    {c.label}
                  </MenuItem>
                ))}
              </TextField>
              {masivo.campo === "suplidor" || masivo.campo === "empresa" ? (
                <TextField
                  select
                  size="small"
                  label="Valor"
                  value={masivo.valor}
                  onChange={(e) => setMasivo((m) => ({ ...m, valor: e.target.value }))}
                  sx={{ ...fpInputSx, minWidth: 220 }}
                >
                  {masivo.campo === "suplidor" && <MenuItem value="">{SIN_SUPLIDOR}</MenuItem>}
                  {(masivo.campo === "suplidor"
                    ? suplidorOptions.map((s) => ({ value: s.id, label: s.label }))
                    : (config.empresas || []).map((e) => ({
                        value: String(e.empresa),
                        label: `${e.empresa} — ${e.nombre || `Empresa ${e.empresa}`}`,
                      }))
                  ).map((o) => (
                    <MenuItem key={o.value} value={o.value}>
                      {o.label}
                    </MenuItem>
                  ))}
                </TextField>
              ) : (
                <TextField
                  size="small"
                  label="Valor"
                  type={
                    ["costo", "precio", "mprecio", "ano"].includes(masivo.campo) ? "number" : "text"
                  }
                  value={masivo.valor}
                  onChange={(e) => setMasivo((m) => ({ ...m, valor: e.target.value }))}
                  sx={fpInputSx}
                />
              )}
              <MDButton
                variant="outlined"
                color="info"
                onClick={aplicarATodas}
                disabled={guardando || filas.length === 0}
              >
                Aplicar a todas
              </MDButton>
            </Stack>
            <Box sx={{ height: 480 }}>
              <DataGrid
                rows={filas}
                columns={columnasDetalle}
                loading={cargandoDetalle}
                processRowUpdate={processRowUpdate}
                onProcessRowUpdateError={(e) =>
                  setError(e?.message || "No se pudo editar la fila.")
                }
                isCellEditable={({ row, field }) => !guardando && !bloqueoCampo(row, field)}
                getRowClassName={({ row }) => {
                  if (erroresPorChasis[row.chasis]) return "fila-error";
                  if (originales[row.chasis] && !mismaFila(row, originales[row.chasis]))
                    return "fila-cambiada";
                  return "";
                }}
                density="compact"
                disableRowSelectionOnClick
                localeText={{ noRowsLabel: "El lote no tiene chasis" }}
                sx={{
                  "& .fila-cambiada": { bgcolor: "rgba(255, 193, 7, 0.18)" },
                  "& .fila-error": { bgcolor: "rgba(244, 67, 54, 0.15)" },
                }}
              />
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
              Doble clic en una celda para editarla. Las filas en amarillo tienen cambios sin
              guardar. Un chasis con préstamo activo no se puede editar; uno vendido conserva costo
              y precios.
            </Typography>
          </>
        )}
      </DialogContent>
      <DialogActions>
        {lote && (
          <>
            <MDButton
              variant="outlined"
              color="dark"
              onClick={deshacer}
              disabled={guardando || sucios.length === 0}
            >
              Deshacer cambios
            </MDButton>
            <MDButton
              variant="gradient"
              color="success"
              onClick={guardar}
              disabled={guardando || sucios.length === 0}
            >
              {guardando ? "Guardando..." : `Guardar cambios (${sucios.length})`}
            </MDButton>
          </>
        )}
        <MDButton variant="outlined" color="dark" onClick={cerrar} disabled={guardando}>
          Cerrar
        </MDButton>
      </DialogActions>
    </Dialog>
  );
}

LotesProductosImportados.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func,
  sucursalOptions: PropTypes.arrayOf(PropTypes.object),
  suplidorOptions: PropTypes.arrayOf(PropTypes.object),
};

LotesProductosImportados.defaultProps = {
  onClose: undefined,
  sucursalOptions: [],
  suplidorOptions: [],
};

export default LotesProductosImportados;
